import type { FontScale } from './types';

export const WIZARD_STAGES = [
  'capture',
  'define',
  'compose',
  'export',
] as const;

export const WIZARD_STAGE_LABELS: Record<string, string> = {
  capture: 'Capture',
  define: 'Define',
  compose: 'Compose',
  export: 'Export',
};

export const WIZARD_STAGE_DESCRIPTIONS: Record<string, string> = {
  capture: 'Record or type your story',
  define: 'Set up characters, places and objects, and style',
  compose: 'Lay out spreads, generate art, place text',
  export: 'Choose format and generate PDF',
};

// ============================================================
// Compose — spread canvas reference units
// ============================================================

/**
 * Reference spread canvas size. Element font sizes are stored in these
 * units (px at a SPREAD_DESIGN_W-wide spread) so text scales with the
 * rendered spread width everywhere — editor, preview, and PDF.
 */
export const SPREAD_DESIGN_W = 1100;
export const SPREAD_DESIGN_H = 740;

/** Default font size (design units) for new text elements. */
export const DEFAULT_TEXT_ELEMENT_SIZE = 27;

/** Line height used for text elements everywhere (editor CSS + PDF +
 *  page-capacity estimate). */
export const SPREAD_TEXT_LINE_HEIGHT = 1.42;

/**
 * The book-level text sizes offered in Define → Typography. `size` is in
 * spread design units (px at a SPREAD_DESIGN_W-wide spread), the same unit
 * `SpreadTextElement.size` is stored in, so the chosen scale IS the size
 * new text blocks start at on the Compose canvas.
 */
export const FONT_SCALE_OPTIONS: Array<{
  value: FontScale;
  label: string;
  size: number;
}> = [
  { value: 'small', label: 'Small', size: 22 },
  { value: 'medium', label: 'Medium', size: DEFAULT_TEXT_ELEMENT_SIZE },
  { value: 'large', label: 'Large', size: 33 },
  { value: 'x-large', label: 'X-Large', size: 40 },
];

/** Design-unit text size for a project's font scale ('medium' fallback). */
export function textSizeForFontScale(
  scale: FontScale | null | undefined,
): number {
  return (
    FONT_SCALE_OPTIONS.find((o) => o.value === scale)?.size ??
    DEFAULT_TEXT_ELEMENT_SIZE
  );
}

/** Outward pad of the text 'panel' readability effect, in design units of
 *  the canvas width. Single source for the editor CSS inset, the PDF's
 *  panel rectangle, and the print-safety footprint checks. */
export const SPREAD_TEXT_PANEL_PAD = 14;

/** Images generated into the bank per request — small/large take counts. */
export const BANK_GENERATE_COUNTS = [2, 4] as const;
export const MAX_BANK_GENERATE_COUNT = 4;

export const STYLE_PRESETS = [
  {
    value: 'watercolor',
    label: 'Watercolor Storybook',
    description: 'Soft, flowing watercolor illustrations with gentle color blending',
    promptFragment: 'Soft watercolor storybook illustration with gentle color blending and paper texture',
  },
  {
    value: 'colored-pencil',
    label: 'Colored Pencil / Crayon',
    description: 'Hand-drawn look with visible pencil or crayon textures',
    promptFragment: 'Hand-drawn colored pencil illustration with visible pencil textures and warm tones',
  },
  {
    value: 'digital-cartoon',
    label: 'Digital Cartoon (Pixar-like)',
    description: 'Clean, 3D-inspired digital art with vibrant colors',
    promptFragment: 'Clean Pixar-style 3D digital cartoon illustration with vibrant colors',
  },
  {
    value: 'flat-vector',
    label: 'Flat Vector Illustration',
    description: 'Modern, clean vector art with bold shapes and colors',
    promptFragment: 'Modern flat vector illustration with bold geometric shapes and bright colors',
  },
  {
    value: 'papercut',
    label: 'Papercut / Collage',
    description: 'Layered paper textures with cut-out shapes and depth',
    promptFragment: 'Layered papercut collage illustration with cut-out shapes and visible paper textures',
  },
  {
    value: 'ink-wash',
    label: 'Ink & Wash',
    description: 'Traditional ink drawings with watercolor washes',
    promptFragment: 'Traditional ink and wash illustration with flowing brush strokes and watercolor washes',
  },
  {
    value: 'custom',
    label: 'Custom Style',
    description: 'Describe your own art style',
    promptFragment: null,
  },
] as const;

/**
 * Resolve the text fragment to inject after "Art style:" into an image-
 * generation prompt. The selected preset is the source of truth:
 *   • a concrete preset → that preset's promptFragment (raw value as a
 *     bare fallback for unknown presets);
 *   • 'custom' (or no preset) → project.style_prompt_fragment — the
 *     user-typed or reference-derived custom style text.
 * style_prompt_fragment survives switching to a preset and back; it is
 * simply inactive while a concrete preset is selected.
 * Returns `null` when the project has no style info, so callers can skip
 * emitting the "Art style:" line entirely.
 */
export function resolveStylePromptFragment(project: {
  style_prompt_fragment: string | null;
  style_preset: string | null;
}): string | null {
  if (project.style_preset && project.style_preset !== 'custom') {
    const preset = STYLE_PRESETS.find((p) => p.value === project.style_preset);
    return preset?.promptFragment ?? project.style_preset;
  }
  return project.style_prompt_fragment || null;
}

export const FORMAT_PRESETS = [
  { value: 'square-8x8', label: 'Square 8×8 in', width: 8, height: 8, description: 'Classic children\'s book format' },
  { value: 'landscape-11x8.5', label: 'Landscape 11×8.5 in', width: 11, height: 8.5, description: 'Wide spread-style layout' },
  { value: 'a4-portrait', label: 'A4 Portrait', width: 8.27, height: 11.69, description: 'Standard A4 paper size' },
  { value: 'a5-portrait', label: 'A5 Portrait', width: 5.83, height: 8.27, description: 'Compact A5 booklet' },
  { value: 'us-letter', label: 'US Letter', width: 8.5, height: 11, description: 'Standard US letter size' },
] as const;

export const MM_PER_INCH = 25.4;

/**
 * Physical page size (inches) for a format preset, with the same 8×8
 * fallback everywhere — print-safety margins, image sizing/sharpness, and
 * the size-preset schematics must all agree on the page they measure.
 */
export function formatPageInches(
  formatPreset: string | null | undefined,
): { w: number; h: number } {
  const preset = FORMAT_PRESETS.find((p) => p.value === formatPreset);
  return { w: preset?.width ?? 8, h: preset?.height ?? 8 };
}

export const TEXT_REFINE_OPTIONS = [
  { value: 'simplify', label: 'Simplify' },
  { value: 'vivid', label: 'Make more vivid' },
  { value: 'shorten', label: 'Shorten' },
  { value: 'age-3-5', label: 'Adjust for age 3–5' },
  { value: 'age-6-8', label: 'Adjust for age 6–8' },
] as const;

/** Readability treatments for text placed over busy art (Compose toolbar). */
export const TEXT_EFFECT_OPTIONS = [
  { value: 'none', label: 'Plain' },
  { value: 'panel', label: 'Paper panel' },
  { value: 'halo', label: 'Outline halo' },
  { value: 'shadow', label: 'Soft shadow' },
] as const;

/**
 * Edge treatments for placed images (Compose toolbar). The lineup follows
 * picture-book conventions: full-bleed/framed plates stay sharp; spot art
 * floating on the page traditionally gets a vignette, feathered fade, or
 * painted-wash border; collage-style books use torn-paper edges.
 */
export const IMAGE_EDGE_OPTIONS = [
  { value: 'sharp', label: 'Sharp', hint: 'Crisp frame — full-bleed art' },
  { value: 'rounded', label: 'Rounded corners', hint: 'Softened frame' },
  { value: 'feather', label: 'Feathered fade', hint: 'Melts into the page' },
  { value: 'vignette', label: 'Oval vignette', hint: 'Classic storybook spot art' },
  { value: 'torn', label: 'Torn paper', hint: 'Deckled collage edge' },
  { value: 'brush', label: 'Painted wash', hint: 'Loose watercolor border' },
  { value: 'scallop', label: 'Cloud scallop', hint: 'Puffy dream frame' },
] as const;

/**
 * Cutout shapes for placed images (Compose toolbar). Diagonal panel cuts
 * echo dynamic comic layouts; organic silhouettes echo scrapbook
 * scissors-and-paste spot art. 'none' restores the full rectangle.
 */
export const IMAGE_SHAPE_OPTIONS = [
  { value: 'none', label: 'Full picture', hint: 'No cutout — the whole rectangle' },
  { value: 'slant', label: 'Slanted cut', hint: 'One diagonal edge — split panel' },
  { value: 'parallelogram', label: 'Lean panel', hint: 'Both sides slanted — dynamic panel' },
  { value: 'shard', label: 'Shard', hint: 'Irregular four-sided cut' },
  { value: 'wedge', label: 'Wedge', hint: 'Sharp triangular sliver' },
  { value: 'wave', label: 'Wave cut', hint: 'Rolling wavy edge' },
  { value: 'blob', label: 'Blob', hint: 'Organic spot-art silhouette' },
  { value: 'arch', label: 'Arch', hint: 'Storybook doorway' },
  { value: 'ellipse', label: 'Oval', hint: 'Clean oval cameo' },
] as const;

/** Valid `SpreadImageShape.kind` values (IMAGE_SHAPE_OPTIONS minus 'none'). */
export const IMAGE_SHAPE_KINDS = IMAGE_SHAPE_OPTIONS.map((o) => o.value).filter(
  (v): v is Exclude<(typeof IMAGE_SHAPE_OPTIONS)[number]['value'], 'none'> => v !== 'none',
);

/**
 * Maximum number of reference images attached to a single Gemini call.
 *
 * With characters (1-3), place (1), objects (0-2), cross-page refs (0-2),
 * and a project style sheet (0-1) all competing for slots, 4 is too tight.
 * 6 preserves the most intentional references while staying under Gemini's
 * practical prompt-dilution threshold.
 */
export const MAX_REFERENCE_IMAGES = 6;

// The split-slider era (target page counts, words-per-page density
// guidance) is gone: the splitter detects natural beats and pagination
// lives on the story rail (see beats.ts). `projects.split_target_pages`
// remains as a legacy column only.
